// Continuidad del negocio (PE-42) y transferencia de riesgos (PE-A4). Lógica pura; se guarda en emergency_plans (M37) con guardarContinuidadYTransferencia (pe-datos.ts).
//  - Continuidad (DE 394, Art. 5 núm. 3): procesos o servicios críticos con su tiempo máximo tolerable de
//    interrupción (horas), recursos y alternativas (sitio alterno, proveedores, equipos), respaldo de información,
//    responsables y contactos clave y pasos para reanudar. Lo que falte queda "por validar" y va al anexo de
//    pendientes del PDF; no impide aprobar.
//  - Transferencia de riesgos (DE 394, Art. 5 núm. 5, "cuando corresponda"): sí/no, tipo, aseguradora y vigencia.
//    Informativo: no tiene estado de cumplimiento.
// La cita del DE 394 es la de la orden PE-A4: el texto del decreto aún no está transcrito en docs/normativa
// (README: sin copia oficial descargable). No se citan otros artículos.

import type { FilaPlan } from './pe-datos';
import type { Traza } from './pe-tipos';
import { numEc } from './pe-unidades';

export const NORMA_DE394 = 'DE 394 (Reglamento General de la Ley GIRD, R.O. 646 5.º Supl., 18/09/2024)';
export const ART_CONTINUIDAD = 'Art. 5 núm. 3';
export const ART_TRANSFERENCIA = 'Art. 5 núm. 5 (cuando corresponda)';
export const FUENTE_DE394 = 'Decreto Ejecutivo 394, Art. 5 núm. 3 (por validar)'; // interno: orden PE-A4 (DE 394 sin transcribir en docs/normativa; ver README)
export const PASO_CONTINUIDAD = 1;

export interface ProcesoCritico { nombre: string; horas: number | null }
export interface ContactoClave { nombre: string; cargo: string; telefono: string }
export interface Continuidad {
  procesos: ProcesoCritico[];
  sitio_alterno: string;
  proveedores: string;
  equipos: string;
  respaldo: string;
  contactos: ContactoClave[];
  pasos: string[];
}

export const continuidadVacia = (): Continuidad =>
  ({ procesos: [], sitio_alterno: '', proveedores: '', equipos: '', respaldo: '', contactos: [], pasos: [] });

const txt = (v: unknown) => (typeof v === 'string' ? v : '');
const numONull = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

// El jsonb guardado → objeto completo (tolera campos faltantes o de otro tipo).
export function continuidadDe(raw: unknown): Continuidad {
  const r = (raw && typeof raw === 'object' && !Array.isArray(raw) ? raw : {}) as Record<string, unknown>;
  const lista = (v: unknown) => (Array.isArray(v) ? v : []);
  return {
    procesos: lista(r.procesos).map((p: any) => ({ nombre: txt(p?.nombre), horas: numONull(p?.horas) })),
    sitio_alterno: txt(r.sitio_alterno), proveedores: txt(r.proveedores), equipos: txt(r.equipos), respaldo: txt(r.respaldo),
    contactos: lista(r.contactos).map((c: any) => ({ nombre: txt(c?.nombre), cargo: txt(c?.cargo), telefono: txt(c?.telefono) })),
    pasos: lista(r.pasos).map(txt),
  };
}

// Sin filas vacías ni espacios sobrantes. Lo que se guarda.
export function limpiarContinuidad(c: Continuidad): Continuidad {
  return {
    procesos: c.procesos.map(p => ({ nombre: p.nombre.trim(), horas: p.horas })).filter(p => p.nombre || p.horas !== null),
    sitio_alterno: c.sitio_alterno.trim(), proveedores: c.proveedores.trim(), equipos: c.equipos.trim(), respaldo: c.respaldo.trim(),
    contactos: c.contactos.map(x => ({ nombre: x.nombre.trim(), cargo: x.cargo.trim(), telefono: x.telefono.trim() }))
      .filter(x => x.nombre || x.cargo || x.telefono),
    pasos: c.pasos.map(p => p.trim()).filter(Boolean),
  };
}

export function validarContinuidad(c: Continuidad): string[] {
  const e: string[] = [];
  const l = limpiarContinuidad(c);
  l.procesos.forEach((p, i) => {
    if (!p.nombre) e.push(`Proceso ${i + 1}: falta el nombre.`);
    if (p.horas !== null && !(p.horas > 0 && p.horas <= 8760)) e.push(`Proceso ${i + 1}: el tiempo máximo tolerable debe estar entre 0 y 8.760 horas.`);
  });
  l.contactos.forEach((x, i) => { if (!x.nombre) e.push(`Contacto ${i + 1}: falta el nombre.`); });
  return e;
}

// Partes del Art. 5 núm. 3 que faltan (vacío = completa).
export function faltantesContinuidad(c: Continuidad | null): string[] {
  const l = limpiarContinuidad(c ?? continuidadVacia());
  const f: string[] = [];
  if (l.procesos.length === 0) f.push('procesos o servicios críticos');
  else if (l.procesos.some(p => p.horas === null)) f.push('tiempo máximo tolerable de interrupción (horas) de cada proceso');
  if (!l.sitio_alterno && !l.proveedores && !l.equipos) f.push('recursos y alternativas (sitio alterno, proveedores o equipos)');
  if (!l.respaldo) f.push('respaldo de información');
  if (l.contactos.length === 0) f.push('responsables y contactos clave');
  if (l.pasos.length === 0) f.push('pasos para reanudar operaciones');
  return f;
}

export function continuidadVaciaDel(c: Continuidad | null): boolean {
  const l = limpiarContinuidad(c ?? continuidadVacia());
  return !l.procesos.length && !l.contactos.length && !l.pasos.length && !l.sitio_alterno && !l.proveedores && !l.equipos && !l.respaldo;
}

// Trazas para el anexo de pendientes: una por parte que falta; si está todo vacío, una sola.
export function trazasContinuidad(c: Continuidad | null): Traza[] {
  const falta = faltantesContinuidad(c);
  const t = (nota: string): Traza => ({ metodo: 'continuidad', norma: NORMA_DE394, articulo_o_tabla: ART_CONTINUIDAD, umbral: null, valor: null,
    unidad: '—', estado: 'por_validar', fuente_doc: FUENTE_DE394, nota, paso: PASO_CONTINUIDAD });
  if (continuidadVaciaDel(c)) return [t('Continuidad del negocio sin llenar (procesos críticos, recursos y alternativas, respaldo, contactos y pasos para reanudar).')];
  return falta.map(f => t(`Continuidad del negocio: falta ${f}.`));
}

// Líneas para la vista previa y el PDF.
export function lineasContinuidad(c: Continuidad | null): { texto: string; porValidar?: boolean }[] {
  if (continuidadVaciaDel(c)) return [{ texto: 'Sin llenar: queda por validar (anexo de pendientes).', porValidar: true }];
  const l = limpiarContinuidad(c as Continuidad);
  const out: { texto: string; porValidar?: boolean }[] = [];
  const pv = (s: string) => out.push({ texto: `${s}: por validar`, porValidar: true });
  if (l.procesos.length) l.procesos.forEach(p => out.push({
    texto: `Proceso crítico: ${p.nombre} · tiempo máximo tolerable: ${p.horas !== null ? `${numEc(p.horas, 1)} h` : 'por validar'}`, porValidar: p.horas === null,
  }));
  else pv('Procesos o servicios críticos');
  if (l.sitio_alterno || l.proveedores || l.equipos) {
    if (l.sitio_alterno) out.push({ texto: `Sitio alterno: ${l.sitio_alterno}` });
    if (l.proveedores) out.push({ texto: `Proveedores alternos: ${l.proveedores}` });
    if (l.equipos) out.push({ texto: `Equipos de respaldo: ${l.equipos}` });
  } else pv('Recursos y alternativas');
  if (l.respaldo) out.push({ texto: `Respaldo de información: ${l.respaldo}` }); else pv('Respaldo de información');
  if (l.contactos.length) l.contactos.forEach(x => out.push({ texto: `Contacto clave: ${[x.nombre, x.cargo, x.telefono].filter(Boolean).join(' · ')}` }));
  else pv('Responsables y contactos clave');
  if (l.pasos.length) l.pasos.forEach((p, i) => out.push({ texto: `Paso ${i + 1} para reanudar: ${p}` }));
  else pv('Pasos para reanudar operaciones');
  return out;
}

// ── Transferencia de riesgos ──

export interface Transferencia { tiene: boolean | null; tipo: string; aseguradora: string; vigenteHasta: string }

export const transferenciaDe = (p: Pick<FilaPlan, 'risk_transfer' | 'risk_transfer_type' | 'risk_transfer_insurer' | 'risk_transfer_valid_until'> | null): Transferencia => ({
  tiene: p?.risk_transfer ?? null, tipo: p?.risk_transfer_type ?? '', aseguradora: p?.risk_transfer_insurer ?? '', vigenteHasta: p?.risk_transfer_valid_until ?? '',
});

export function validarTransferencia(t: Transferencia): string[] {
  const e: string[] = [];
  if (t.vigenteHasta && !/^\d{4}-\d{2}-\d{2}$/.test(t.vigenteHasta)) e.push('La vigencia de la póliza no es una fecha válida.');
  if (t.tipo.length > 120 || t.aseguradora.length > 120) e.push('Tipo y aseguradora: hasta 120 caracteres.');
  return e;
}

export function lineasTransferencia(t: Transferencia, hoy = new Date()): string[] {
  if (t.tiene === null) return ['Sin responder (informativo; el DE 394 lo pide "cuando corresponda").'];
  if (!t.tiene) return ['No tiene póliza ni otro mecanismo de transferencia de riesgos (informativo).'];
  const hoyIso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
  const vence = t.vigenteHasta ? `vigente hasta ${t.vigenteHasta}${t.vigenteHasta < hoyIso ? ' (vencida)' : ''}` : 'vigencia sin indicar';
  return [`Sí: ${t.tipo.trim() || 'tipo sin indicar'} · ${t.aseguradora.trim() || 'aseguradora sin indicar'} · ${vence} (informativo).`];
}
